import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useCompanyCallRingSettings, type RingStrategy } from '@/hooks/useCompanyCallRingSettings';
import { useAppUsers } from '@/hooks/useAppUsers';
import { ArrowDown, ArrowUp, Loader2, Phone, Users, X } from 'lucide-react';
import { MultiSelect } from '@/components/ui/multi-select';

const STRATEGY_OPTIONS: { value: RingStrategy; label: string; hint: string }[] = [
  {
    value: 'simultaneous',
    label: 'Todos ao mesmo tempo',
    hint: 'A ligação toca para todos os selecionados ao mesmo tempo; quem atender primeiro assume.',
  },
  {
    value: 'sequential',
    label: 'Em sequência',
    hint: 'Toca em um por vez, na ordem em que foram selecionados, até alguém atender.',
  },
];

export function CompanyCallRingSettingsPanel() {
  const { settings, isLoading, updateSettings, isUpdating } = useCompanyCallRingSettings();
  const { users } = useAppUsers();

  const [userIds, setUserIds] = useState<string[]>([]);
  const [strategy, setStrategy] = useState<RingStrategy>('simultaneous');
  const [timeout, setTimeoutSeconds] = useState(30);

  useEffect(() => {
    const current = settings?.find((s) => s.level === 1);
    if (current) {
      setUserIds(current.user_ids ?? []);
      setStrategy((current.ring_strategy as RingStrategy) ?? 'simultaneous');
      setTimeoutSeconds(current.timeout_seconds ?? 30);
    }
  }, [settings]);

  const userOptions = useMemo(
    () =>
      (users || [])
        .filter((user: any) => user.active)
        .map((user: any) => ({
          value: user.id,
          label: user.display_name || `${user.first_name} ${user.last_name}`,
        })),
    [users]
  );

  const userNameById = useMemo(
    () => new Map(userOptions.map((o) => [o.value, o.label])),
    [userOptions]
  );

  const moveUser = (index: number, delta: number) => {
    const next = [...userIds];
    const target = index + delta;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    setUserIds(next);
  };

  const handleSave = () => {
    updateSettings([
      {
        level: 1,
        name: 'Atendimento',
        user_ids: userIds,
        ring_strategy: strategy,
        timeout_seconds: timeout,
      },
    ]);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Atendimento do Número da Empresa</CardTitle>
        <CardDescription>
          Escolha quem recebe as ligações que chegam no número da empresa e como elas tocam.
          Números pessoais não são afetados.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border p-3">
          <p className="text-sm text-muted-foreground">
            Cadastro dos números e escolha do número principal ficam na central de números.
          </p>
          <Button variant="outline" size="sm" asChild>
            <Link to="/settings/communication/phone-numbers">
              <Phone className="h-4 w-4 mr-2" />
              Números da Empresa
            </Link>
          </Button>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Quem atende</Label>
              <MultiSelect
                options={userOptions}
                value={userIds}
                onChange={setUserIds}
                placeholder="Selecione os usuários do atendimento"
              />
            </div>

            {userIds.length > 0 && (
              <div className="space-y-2">
                <Label>
                  {strategy === 'sequential' ? 'Ordem de toque' : 'Atendentes selecionados'}
                </Label>
                <div className="rounded-lg border divide-y">
                  {userIds.map((id, i) => (
                    <div key={id} className="flex items-center gap-3 p-3">
                      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
                        {strategy === 'sequential' ? i + 1 : <Users className="h-3.5 w-3.5" />}
                      </span>
                      <span className="flex-1 text-sm font-medium">
                        {userNameById.get(id) ?? 'Usuário'}
                      </span>
                      {strategy === 'sequential' && (
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            disabled={i === 0}
                            onClick={() => moveUser(i, -1)}
                            aria-label="Subir na ordem"
                          >
                            <ArrowUp className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            disabled={i === userIds.length - 1}
                            onClick={() => moveUser(i, 1)}
                            aria-label="Descer na ordem"
                          >
                            <ArrowDown className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => setUserIds(userIds.filter((u) => u !== id))}
                        aria-label="Remover"
                      >
                        <X className="h-4 w-4 text-muted-foreground" />
                      </Button>
                    </div>
                  ))}
                </div>
                {strategy === 'sequential' && (
                  <p className="text-xs text-muted-foreground">
                    A ligação toca nesta ordem, {timeout}s em cada pessoa, até alguém atender.
                  </p>
                )}
              </div>
            )}


            <div className="space-y-2">
              <Label>Como tocar</Label>
              <Select value={strategy} onValueChange={(v) => setStrategy(v as RingStrategy)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STRATEGY_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {STRATEGY_OPTIONS.find((o) => o.value === strategy)?.hint}
              </p>
            </div>

            <div className="space-y-2">
              <Label>
                {strategy === 'sequential'
                  ? 'Tempo por usuário (segundos)'
                  : 'Tempo de toque (segundos)'}
              </Label>
              <Input
                type="number"
                min="10"
                max="120"
                value={timeout}
                onChange={(e) => setTimeoutSeconds(parseInt(e.target.value) || 30)}
              />
            </div>

            <Button onClick={handleSave} disabled={isUpdating} className="w-full">
              {isUpdating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Salvar Configuração
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
